/** @forge-header
 * @id          soccerParser
 * @role        Soccer ticker parser | KXUECLGAME / KXWCGAME / KXINTLFRIENDLYGAME 3-way game moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:parseFallback,splitCompetitors,stripTimePrefix
 * @downstream  kalshiTicker/parse
 * @exports     parseSoccerGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitCompetitors, stripTimePrefix, isDrawOutcome } from '../helpers';

/**
 * Soccer: KXUECLGAME-26MAY27CRYRVC-CRY | KXWCGAME-26JUN27JORARG-TIE
 * | KXINTLFRIENDLYGAME-26JUN08FRAIRL-FRA
 * 3-way (Home / Away / Draw) — one ticker per outcome. The marketStore
 * multi-outcome builder groups the three tickers by event; this lib parser
 * exists for display (TickerDisplay chip + describeMarket label on positions).
 * For the Draw ticker the outcome doesn't bound either team, so we mid-split
 * the (consistent) team chunk to recover away/home for the label.
 */
export function parseSoccerGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const series = parts[0] ?? 'KXSOCCER';
  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;
  const teamsPart = stripTimePrefix(mid.slice(7));

  let awayCode: string;
  let homeCode: string;
  if (outcome && !isDrawOutcome(outcome)) {
    [awayCode, homeCode] = splitCompetitors(teamsPart, outcome);
  } else {
    const half = Math.floor(teamsPart.length / 2);
    awayCode = teamsPart.slice(0, half);
    homeCode = teamsPart.slice(half);
  }

  const game: GameInfo = { awayCode, homeCode };
  const outcomeLabel = isDrawOutcome(outcome) ? 'Draw' : outcome ? `${outcome} Win` : null;
  const shortLabel = `Soccer: ${awayCode} v ${homeCode}`;
  const fullLabel = `Soccer: ${awayCode} v ${homeCode}${outcomeLabel ? ` · ${outcomeLabel}` : ''}`;

  return {
    raw,
    category: 'soccer-game',
    series,
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/**
 * Soccer knockout "To Advance": KXWCADVANCE-26JUL01ENGCOD-ENG |
 * KXUECLADVANCE-…. A 2-way (the two teams, NO draw) settled on who advances
 * incl. extra time + penalties. One ticker per team — the outcome is always a
 * team code (never TIE), so we split the team chunk on it like a moneyline.
 * Reuses the 'soccer-game' category (no new color-map entry); the
 * 'KXSOCCERADVANCE' series marker lets describeMarket tag the bet team as
 * "<TEAM> Adv" so a held position reads apart from the regulation 1X2 winner.
 */
export function parseSoccerAdvance(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null; // a team code (never a draw)
  const teamsPart = stripTimePrefix(mid.slice(7));

  let awayCode: string;
  let homeCode: string;
  if (outcome) {
    [awayCode, homeCode] = splitCompetitors(teamsPart, outcome);
  } else {
    const half = Math.floor(teamsPart.length / 2);
    awayCode = teamsPart.slice(0, half);
    homeCode = teamsPart.slice(half);
  }

  const game: GameInfo = { awayCode, homeCode };
  const advLabel = outcome ? `${outcome} Advances` : 'To Advance';
  const shortLabel = `Soccer: ${awayCode} v ${homeCode} · To Advance`;
  const fullLabel = `Soccer: ${awayCode} v ${homeCode} · ${advLabel}`;

  return {
    raw,
    category: 'soccer-game',
    series: 'KXSOCCERADVANCE',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/**
 * Soccer BTTS (both teams to score): KXWCBTTS-26JUN18SUIBIH-BTTS |
 * KXWC1HBTTS-… | KXWC2HBTTS-…. A single binary per game/period — no full-game
 * spread/total/game equivalent, so it parses directly (parse.ts dispatches the
 * BTTS prefix here). Outcome is always 'BTTS'; the period (if any) annotates the
 * side label. Mid-splits the team chunk for the away/home label, like the Draw
 * case above. Reuses the 'soccer-game' category to avoid a new color-map entry.
 */
export function parseSoccerBtts(
  raw: string,
  rest: string,
  period?: ParsedTicker['period'],
): ParsedTicker {
  const mid = (rest.split('-')[0] ?? '');
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamsPart = stripTimePrefix(mid.slice(7));
  const half = Math.floor(teamsPart.length / 2);
  const awayCode = teamsPart.slice(0, half);
  const homeCode = teamsPart.slice(half);

  const game: GameInfo = { awayCode, homeCode };
  const periodLabel = period === '1H' ? ' · 1st Half' : period === '2H' ? ' · 2nd Half' : '';
  const shortLabel = `Soccer: ${awayCode} v ${homeCode} · BTTS`;
  const fullLabel = `Soccer: ${awayCode} v ${homeCode} · Both Teams To Score${periodLabel}`;

  return {
    raw,
    category: 'soccer-game',
    series: 'KXSOCCERBTTS',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: 'BTTS',
    period,
    shortLabel,
    fullLabel,
  };
}
